import React, { FC, LegacyRef } from 'react'
import cn from 'classnames'
import { useTranslation } from 'next-i18next'

// `disabled:bg-gray-6` is load-bearing: the base-layer `input:disabled` rule is
// 0-1-1 and would out-rank a plain `bg-gray-6` on a disabled field.
const FIELD_CONTROL = 'border border-solid border-gray-5 bg-gray-6 text-gray-1 disabled:bg-gray-6'

type TextFieldProps = {
  id?: string,
  type?: string,
  label?: string,
  error?: string,
  value?: string,
  onKeyUp?: () => void,
  onPaste?: () => void,
  innerRef?: LegacyRef<HTMLInputElement | HTMLTextAreaElement>,
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
  required?: boolean,
  disabled?: boolean,
  readOnly?: boolean,
  maxLength?: number,
  step?: string,
  className?: string,
  onKeyDown?: (event?: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
  autoFocus?: boolean,
  placeholder?: string,
  defaultValue?: string,
  autoComplete?: string,
  subLabel?: string,
  name?: string,
}

// not currently supporting success due to no use cases
// in spirit we always support success
const TextField:FC<TextFieldProps> = ({
  id,
  type = 'text',
  label,
  error,
  value,
  onKeyUp,
  onPaste,
  innerRef,
  onChange,
  required,
  disabled,
  readOnly,
  maxLength,
  step,
  className,
  onKeyDown,
  autoFocus,
  placeholder,
  defaultValue,
  autoComplete,
  subLabel,
  name,
}) => {
  const { t } = useTranslation('common')

  return (
    <div className="field-container flex flex-col [&_select]:placeholder:text-gray-4 [&_input]:placeholder:text-gray-4 [&_textarea]:placeholder:text-gray-4">
      {label && (
      <span className="labels flex">
        <label htmlFor={id} className="text-gray-3">{label}</label>
        {!required && (
        <h5 className="optional-label ms-1 text-gray-4">
          -
          {t('optional', '- optional')}
        </h5>
        )}
        {subLabel && <div className="small-caption-text ms-auto text-gray-3">{subLabel}</div>}
      </span>
      )}
      {type === 'textarea' ? (
        <textarea
          id={id}
          name={id}
          ref={innerRef as LegacyRef<HTMLTextAreaElement>}
          onKeyUp={onKeyUp}
          onChange={onChange}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          className={cn(className, FIELD_CONTROL)}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          autoComplete={autoComplete}
        />
      ) : (
        <input
          id={id}
          name={name || id}
          type={type}
          value={value}
          ref={innerRef as LegacyRef<HTMLInputElement>}
          onKeyUp={onKeyUp}
          onPaste={onPaste}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={maxLength}
          step={step}
          onChange={onChange}
          className={cn(className, FIELD_CONTROL)}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          placeholder={placeholder}
          defaultValue={defaultValue}
          autoComplete={autoComplete}
        />
      )}
      {error && <div className="error mt-[8px] font-[Switzer] text-[12px] font-normal not-italic leading-[110%] [color:#e4635c]">{error}</div>}
    </div>
  )
}

export default TextField
